import { useState, useCallback, useContext, useRef, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EditorActions } from '@atlaskit/editor-core/actions';

import {
	REFRESH_EDITOR_CONTENT_EXPERIENCE,
	IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	EDIT_PAGE_V2,
	EDIT_PAGE_EMBED,
	VIEW_PAGE,
} from '@confluence/named-routes/entry-points/routes.ts';
import type { Route } from '@confluence/route/entry-points/Route.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { refetchEditorContent } from '@confluence/editor-features/entry-points/refetchEditorContent.ts';
import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import {
	getInEditorTemplatesPanelQueryParamValue,
	isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen,
	removeInEditorTemplatesQueryParam,
	setInEditorTemplatesPanelQueryParam,
	isInEditorTemplatesPanelQueryParamValueOpen,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED,
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
	CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME,
	getCreatedWithTemplateQueryParamValue,
	CONTEXT_PANEL_TAB_TARGET,
	CONTEXT_PANEL_TAB_TARGET_TEMPLATES,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
	CONTEXT_PANEL_TAB_TARGET_IMPORT,
	PANEL_MODE_TEMPLATES,
	PANEL_MODE_IMPORT,
	type PanelMode,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const POST_BE_SYNCHRONOUS_REFRESH_EDITOR_TIMEOUT = 1500;
const DELAY_INITIAL_CHECK_TIMEOUT = 4000;

const isContentCreationPanelOpen = (getQueryParams): boolean => {
	return isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen(getQueryParams);
};

type Props = {
	cancelDebouncedSaveDraft: () => void;
	contentId: string;
	draftShareId?: string;
	editorActions: EditorActions;
	hideSpinner: () => void;
	showSpinner: () => void;
	isUnpublishedDraft: boolean | null;
	isEmbeddedEditor?: boolean;
};

export const useEditorContextPanel = ({
	cancelDebouncedSaveDraft,
	contentId,
	draftShareId,
	editorActions,
	hideSpinner,
	showSpinner,
	isUnpublishedDraft,
	isEmbeddedEditor,
}: Props): {
	beforeUpdateRequest: () => void;
	hasUserAddedContent: boolean | null;
	isContentCreationPanelOpen: boolean;
	contentCreationPanelMode: PanelMode | undefined;
	onDocChange: () => void;
	onClickOpenContentCreationPanel: (panelMode?: PanelMode) => void;
	onClickCloseContentCreationPanel: () => void;
	postUpdateRequest: () => void;
	onUpdatedWithTemplateSubscriptionEvent: (response: any) => Promise<void>;
} => {
	const { isRovoLLMEnabled } = useSessionData();
	const { setQueryParams, getQueryParams, replace } = useContext(RoutesContext);
	const _skipLocalChangesCheck = useRef<boolean>(
		getCreatedWithTemplateQueryParamValue(getQueryParams),
	);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [hasUserAddedContent, setHasUserAddedContent] = useState<boolean | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

	useEffect(() => {
		setTimeout(() => {
			_skipLocalChangesCheck.current = false;
		}, DELAY_INITIAL_CHECK_TIMEOUT);
		/* eslint-disable react-hooks/exhaustive-deps */
	}, []);

	const onDocChange = useCallback((): void => {
		const editorHasContent = !editorActions.isDocumentEmpty();
		const wasTemplatesPanelAutoClosed =
			getInEditorTemplatesPanelQueryParamValue(getQueryParams) ===
			IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED;

		if (isUnpublishedDraft && !editorHasContent && wasTemplatesPanelAutoClosed) {
			setInEditorTemplatesPanelQueryParam(setQueryParams, IN_EDITOR_TEMPLATES_QUERY_OPEN);
		}

		if (!_skipLocalChangesCheck.current) {
			if (isInEditorTemplatesPanelQueryParamValueOpen(getQueryParams) && editorHasContent) {
				setQueryParams(
					{
						[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED,
						[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME]: null,
					},
					true,
				);
			}

			setHasUserAddedContent(editorHasContent);
		}
	}, [editorActions, _skipLocalChangesCheck, getQueryParams, setQueryParams, isUnpublishedDraft]);

	useEffect(() => {
		subscribeDocChange(onDocChange);
		return () => unsubscribeDocChange(onDocChange);
	}, [onDocChange]);

	const onClickCloseContentCreationPanel = useCallback((): void => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'templateDrawer',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'templateDismissButton',
			},
		}).fire();

		removeInEditorTemplatesQueryParam(setQueryParams, isRovoLLMEnabled);
	}, [setQueryParams, createAnalyticsEvent, isRovoLLMEnabled]);

	const onClickOpenContentCreationPanel = useCallback(
		(panelMode?: PanelMode): void => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'dropdownMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId:
						panelMode === PANEL_MODE_IMPORT ? 'openImportBrowser' : 'openTemplateBrowser',
				},
			}).fire();

			let tabTarget:
				| typeof CONTEXT_PANEL_TAB_TARGET_IMPORT
				| typeof CONTEXT_PANEL_TAB_TARGET_TEMPLATES
				| typeof CONTEXT_PANEL_TAB_TARGET_BLOCK;
			if (panelMode === PANEL_MODE_IMPORT) {
				tabTarget = CONTEXT_PANEL_TAB_TARGET_IMPORT;
			} else if (
				!editorActions.isDocumentEmpty() &&
				expVal('platform_editor_blocks_conf', 'isEnabled', false)
			) {
				// When the page has content, default to the Block tab so users
				// can quickly insert block-level snippets instead of full-page templates.
				tabTarget = CONTEXT_PANEL_TAB_TARGET_BLOCK;
			} else {
				tabTarget = CONTEXT_PANEL_TAB_TARGET_TEMPLATES;
			}

			setInEditorTemplatesPanelQueryParam(
				setQueryParams,
				IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
				tabTarget,
				isRovoLLMEnabled,
			);
		},
		[setQueryParams, createAnalyticsEvent, isRovoLLMEnabled, editorActions],
	);

	const beforeUpdateRequest = useCallback((): void => {
		showSpinner();
		cancelDebouncedSaveDraft();
		_skipLocalChangesCheck.current = true;
	}, [showSpinner, cancelDebouncedSaveDraft, _skipLocalChangesCheck]);

	const postUpdateRequest = useCallback((): void => {
		setHasUserAddedContent(false);

		setInEditorTemplatesPanelQueryParam(setQueryParams, IN_EDITOR_TEMPLATES_QUERY_OPEN);

		setTimeout(() => {
			_skipLocalChangesCheck.current = false;
			hideSpinner();
		}, POST_BE_SYNCHRONOUS_REFRESH_EDITOR_TIMEOUT);
	}, [hideSpinner, _skipLocalChangesCheck, setQueryParams]);

	const handleUpdatedWithTemplate = useCallback(
		async (payload: any) => {
			try {
				showSpinner();
				experienceTracker.start({
					name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
					timeout: ExperienceTimeout.DEFAULT,
				});
				const { spaceKey, subType } = payload;
				await refetchEditorContent(contentId);

				let route: Route;

				let query = draftShareId ? { draftShareId } : {};

				if (isEmbeddedEditor) {
					route = EDIT_PAGE_EMBED;

					const params = getQueryParams();

					query = {
						...query,
						...params,
					};
				} else if (subType === 'live') {
					route = VIEW_PAGE;
					// Since live page don't have the concept of drafts, draftShareId is not needed
					query = {};
				} else {
					route = EDIT_PAGE_V2;
				}

				replace(
					route.toUrl(
						{
							spaceKey,
							contentId,
							contentType: 'pages',
						},
						{
							query,
						},
					),
				);
				experienceTracker.succeed({
					name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
				});
				experienceTracker.succeed({
					name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
				});
			} catch (error) {
				if (error instanceof Error) {
					experienceTracker.stopOnError({
						name: REFRESH_EDITOR_CONTENT_EXPERIENCE,
						error,
					});
					experienceTracker.stopOnError({
						name: IN_EDITOR_TEMPLATES_UPDATE_CONTENT_REFRESH_EDITOR_EXPERIENCE,
						error,
					});
				}
			} finally {
				hideSpinner();
			}
		},
		[
			contentId,
			cancelDebouncedSaveDraft,
			draftShareId,
			experienceTracker,
			showSpinner,
			hideSpinner,
			replace,
			isEmbeddedEditor,
			getQueryParams,
		],
	);

	const onUpdatedWithTemplateSubscriptionEvent = useCallback(
		async (response: any): Promise<void> => {
			cancelDebouncedSaveDraft();

			await handleUpdatedWithTemplate({
				spaceKey: response?.contentUpdatedWithTemplate?.spaceKey,
				subType: response?.contentUpdatedWithTemplate?.subtype,
			});
		},
		[],
	);

	// Derive the current panel mode from query params
	// 'import' => 'import' mode, 'templates' or 'block' => 'templates' mode
	let contentCreationPanelMode: PanelMode | undefined;
	if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
		const queryParams = getQueryParams();
		const contextPanelTabTarget = queryParams[CONTEXT_PANEL_TAB_TARGET] as string | undefined;

		if (
			contextPanelTabTarget === CONTEXT_PANEL_TAB_TARGET_BLOCK ||
			contextPanelTabTarget === CONTEXT_PANEL_TAB_TARGET_TEMPLATES
		) {
			contentCreationPanelMode = PANEL_MODE_TEMPLATES;
		} else if (contextPanelTabTarget === CONTEXT_PANEL_TAB_TARGET_IMPORT) {
			contentCreationPanelMode = PANEL_MODE_IMPORT;
		}
	}

	// For cwr_blank_object_experiment: derive from tab target query param
	if (isRovoLLMEnabled && expVal('cwr_blank_object_experiment', 'isEnabled', false)) {
		const queryParams = getQueryParams();
		const tabTarget = queryParams[CONTEXT_PANEL_TAB_TARGET] as string | undefined;

		if (tabTarget === CONTEXT_PANEL_TAB_TARGET_IMPORT) {
			contentCreationPanelMode = PANEL_MODE_IMPORT;
		} else if (
			tabTarget === CONTEXT_PANEL_TAB_TARGET_BLOCK ||
			tabTarget === CONTEXT_PANEL_TAB_TARGET_TEMPLATES
		) {
			contentCreationPanelMode = PANEL_MODE_TEMPLATES;
		} else if (tabTarget === undefined) {
			contentCreationPanelMode = undefined;
		}
	}

	return {
		beforeUpdateRequest,
		hasUserAddedContent,
		isContentCreationPanelOpen: isContentCreationPanelOpen(getQueryParams),
		contentCreationPanelMode,
		onDocChange,
		onClickOpenContentCreationPanel,
		onClickCloseContentCreationPanel,
		postUpdateRequest,
		onUpdatedWithTemplateSubscriptionEvent,
	};
};
